import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { FixtureModel } from '@mads/shared'
import { ConfirmDialog, ErrorNote, Loading } from '@/components/Feedback'
import { useCreateFixtureModel, useDeleteFixtureModel, useFixtureModels } from '@/lib/queries'

/** "650W · GLC 650W · 185mm sq" — whatever of the spec has been filled in. */
function specLine(model: FixtureModel): string {
  const parts = [
    model.lightType,
    model.watts === null ? null : `${model.watts}W`,
    model.lamp,
    model.gelFrame,
  ].filter((part): part is string => part !== null && part !== '')

  return parts.length === 0 ? 'No spec recorded yet' : parts.join(' · ')
}

/**
 * The catalogue of fixture makes and models (issue #30). Describing "CCT
 * Fresnel 650W" once here is what stops the same optics being typed out again
 * for every one of them; the spec, the notes and the links to spares live on
 * the model, and items point at it.
 *
 * Not admin-gated: this is kit, and crew run the inventory workflow.
 */
export function Fixtures() {
  const { data: models, isPending, error } = useFixtureModels()

  const create = useCreateFixtureModel()
  const remove = useDeleteFixtureModel()

  const [newName, setNewName] = useState('')
  const [confirming, setConfirming] = useState<FixtureModel | null>(null)

  if (isPending) return <Loading what="Loading fixture models" />
  if (error !== null) return <ErrorNote error={error} />

  return (
    <>
      <h1 className="page-title">Fixture models</h1>
      <p className="page-lede">
        A make and model described once — optics, notes and where to buy spares — with every
        lantern of that kind linked to it.
      </p>

      <form
        className="filter-bar"
        onSubmit={(event) => {
          event.preventDefault()
          if (newName.trim() === '') return
          create.mutate({ name: newName.trim() }, { onSuccess: () => setNewName('') })
        }}
      >
        <div className="field field-inline" style={{ flex: '1 1 260px' }}>
          <label htmlFor="new-fixture-model">New fixture model</label>
          <input
            id="new-fixture-model"
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
            className="input"
            placeholder="e.g. CCT Fresnel 650W"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>
          Add fixture model
        </button>
      </form>

      <ErrorNote error={create.error ?? remove.error} />

      {models.length === 0 ? (
        <p className="empty-note">
          No fixture models yet. Add one above, then link lanterns to it from their own page.
        </p>
      ) : (
        <div className="card-grid">
          {[...models].sort((a, b) =>
            (a.brand ?? '').localeCompare(b.brand ?? '') || a.name.localeCompare(b.name),
          ).map((model) => (
            <div className="card" key={model.id}>
              <div className="card-kicker">
                {model.itemCount} item{model.itemCount === 1 ? '' : 's'} · {model.unitCount} unit
                {model.unitCount === 1 ? '' : 's'}
              </div>

              {/* The display name already leads with the brand (issue #59),
                  so the card body no longer repeats it. */}
              <div className="card-title">{model.displayName}</div>
              <p className="card-body">
                {specLine(model)}
                {model.links.length > 0 && (
                  <>
                    {' · '}
                    {model.links.length} link{model.links.length === 1 ? '' : 's'}
                  </>
                )}
              </p>

              <div className="action-row">
                {/* Lands on the inventory already filtered to this model. */}
                <Link
                  to="/inventory"
                  className="btn btn-ghost"
                  style={{ paddingLeft: 0 }}
                  search={{ fixtureModelId: model.id }}
                >
                  View items
                </Link>
                <Link
                  to="/fixtures/$fixtureModelId"
                  className="btn btn-ghost"
                  params={{ fixtureModelId: String(model.id) }}
                >
                  Open
                </Link>
                <button type="button" className="btn btn-ghost" onClick={() => setConfirming(model)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirming !== null && (
        <ConfirmDialog
          title="Remove this fixture model?"
          body={`"${confirming.displayName}" will be removed. Its ${confirming.itemCount} item(s) stay in the inventory, each keeping a copy of the spec recorded here.`}
          confirmLabel="Remove"
          busy={remove.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            remove.mutate(confirming.id, { onSuccess: () => setConfirming(null) })
          }}
        />
      )}
    </>
  )
}
